import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

interface FormData {
  // 담당자 정보
  name: string;
  email: string;
  password: string;
  passwordConfirm: string;
  phone: string;

  // 조직 정보
  organization_name: string;
  business_registration_number: string;
  business_name: string;
  address: string;
  organization_phone: string;
}

interface FormErrors {
  [key: string]: string;
}

const OwnerSignupPage: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<1 | 2 | 3>(1); // 1: 요금·약관 동의, 2: 담당자 정보, 3: 사업장 정보 (2026-10-27 동의를 첫 단계로)

  // 약관 동의 (필수 4 + 선택 1) — 2026-10-27 대표 지시. 버전은 서버가 붙인다(core/subscription_terms.py)
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedEntrustment, setAgreedEntrustment] = useState(false);
  const [agreedPricing, setAgreedPricing] = useState(false);
  const [agreedMarketing, setAgreedMarketing] = useState(false);
  const allRequiredAgreed = agreedTerms && agreedPrivacy && agreedEntrustment && agreedPricing;

  // 요금 안내 — 값의 원본은 서버(GET /auth/owner/terms-info). 못 받아도 같은 기본값으로 보여 준다.
  const [termsInfo, setTermsInfo] = useState({
    plan_name: '베이직',
    fee_label: '월 500,000원(부가세 별도)',
    effective_date: '2026-11-01',
    support_email: 'admin@newattend.com',
  });
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/v1/auth/owner/terms-info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setTermsInfo((prev) => ({ ...prev, ...d })); })
      .catch(() => {});
  }, []);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
    organization_name: '',
    business_registration_number: '',
    business_name: '',
    address: '',
    organization_phone: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // 이메일 중복 확인 상태
  const [emailCheckStatus, setEmailCheckStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [emailCheckTimer, setEmailCheckTimer] = useState<NodeJS.Timeout | null>(null);

  // 담당자 휴대폰 카톡 인증 상태 (2026-10-27 밤 — 운영 메일 서버가 없어 이메일 인증 대신)
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneVerifiedToken, setPhoneVerifiedToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isCodeSending, setIsCodeSending] = useState(false);
  const [isCodeVerifying, setIsCodeVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  // 페이지 진입 시 만료된 토큰 정리
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000; // 초를 밀리초로 변환
        if (Date.now() >= exp) {
          // 만료된 토큰 삭제
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (error) {
        // 잘못된 토큰 형식이면 삭제
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  // 이메일 중복 확인 API 호출
  const checkEmailAvailability = async (email: string) => {
    if (!email || !/^[\w\.-]+@[\w\.-]+\.\w+$/.test(email)) {
      setEmailCheckStatus('idle');
      return;
    }

    setEmailCheckStatus('checking');

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/owner/check-email?email=${encodeURIComponent(email)}`);
      const data = await response.json();

      if (data.success) {
        setEmailCheckStatus(data.available ? 'available' : 'taken');
      } else {
        setEmailCheckStatus('idle');
      }
    } catch (error) {
      console.error('이메일 확인 오류:', error);
      setEmailCheckStatus('idle');
    }
  };

  // 입력 핸들러
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // 에러 초기화
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // 이메일 필드일 경우 실시간 중복 확인 (debounce)
    if (name === 'email') {
      if (emailCheckTimer) {
        clearTimeout(emailCheckTimer);
      }

      setEmailCheckStatus('idle');

      const timer = setTimeout(() => {
        checkEmailAvailability(value);
      }, 500); // 0.5초 대기 후 확인

      setEmailCheckTimer(timer);
    }
  };

  // 사업자등록번호 자동 포맷팅
  const handleBusinessNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '');

    if (value.length > 10) {
      value = value.slice(0, 10);
    }

    // 포맷: XXX-XX-XXXXX
    if (value.length >= 6) {
      value = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
    } else if (value.length >= 4) {
      value = `${value.slice(0, 3)}-${value.slice(3)}`;
    }

    setFormData(prev => ({ ...prev, business_registration_number: value }));
  };

  // 전화번호 자동 포맷팅
  const handlePhoneChange = (name: 'phone' | 'organization_phone') => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (name === 'phone' && value !== formData.phone.replace(/[^0-9]/g, '')) {
      // 인증한 번호를 바꾸면 다시 인증해야 한다
      setPhoneVerified(false);
      setPhoneVerifiedToken('');
      setCodeSent(false);
      setVerificationCode('');
      setPhoneMsg(null);
    }

    if (value.length > 11) {
      value = value.slice(0, 11);
    }

    // 포맷: 010-XXXX-XXXX / 02-XXX(X)-XXXX / 031-XXX(X)-XXXX
    // (2026-10-27) 종전엔 무조건 3-4-나머지로 잘라 서울(02)·10자리 지역번호가 입력 자체가 안 됐다
    const area = value.startsWith('02') ? 2 : 3;
    const rest = value.slice(area);
    if (rest.length > 7) {
      value = `${value.slice(0, area)}-${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
    } else if (rest.length > 3) {
      value = `${value.slice(0, area)}-${rest.slice(0, 3)}-${rest.slice(3)}`;
    } else if (rest.length > 0) {
      value = `${value.slice(0, area)}-${rest}`;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // 카톡 인증번호 발송 (담당자 휴대폰)
  const handleSendVerificationCode = async () => {
    if (!/^010-\d{4}-\d{4}$/.test(formData.phone)) {
      setPhoneMsg({ type: 'error', text: '휴대폰 번호를 먼저 입력해주세요 (010-XXXX-XXXX)' });
      return;
    }
    setIsCodeSending(true);
    setPhoneMsg(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/owner/send-phone-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ phone: formData.phone.replace(/[^0-9]/g, '') }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || '인증번호를 보내지 못했습니다');
      }
      setCodeSent(true);
      setVerificationCode('');
      setPhoneMsg({ type: 'ok', text: `카카오톡으로 인증번호를 보냈습니다. ${data.valid_minutes || 10}분 안에 입력해주세요.` });
    } catch (error) {
      setPhoneMsg({
        type: 'error',
        text: error instanceof TypeError ? '네트워크 연결을 확인해주세요.' : (error instanceof Error ? error.message : '인증번호 발송 중 오류가 발생했습니다'),
      });
    } finally {
      setIsCodeSending(false);
    }
  };

  // 인증번호 확인
  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      setPhoneMsg({ type: 'error', text: '6자리 인증번호를 입력해주세요' });
      return;
    }
    setIsCodeVerifying(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/owner/verify-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.phone.replace(/[^0-9]/g, ''), code: verificationCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.verified_token) {
        throw new Error(data.detail || '인증번호가 올바르지 않습니다');
      }
      setPhoneVerified(true);
      setPhoneVerifiedToken(data.verified_token);
      setPhoneMsg(null);
      setErrors(prev => ({ ...prev, phone: '' }));
    } catch (error) {
      setPhoneMsg({ type: 'error', text: error instanceof Error ? error.message : '인증 확인 중 오류가 발생했습니다' });
    } finally {
      setIsCodeVerifying(false);
    }
  };

  // 담당자 정보(2단계) 유효성 검사 — 함수 이름은 옛 순서 그대로
  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요';
    }

    const emailRegex = /^[\w\.-]+@[\w\.-]+\.\w+$/;
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해주세요';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }

    if (emailCheckStatus === 'taken') {
      newErrors.email = '이미 사용 중인 이메일입니다';
    }

    if (!formData.password) {
      newErrors.password = '비밀번호를 입력해주세요';
    } else if (formData.password.length < 8) {
      newErrors.password = '비밀번호는 8자 이상이어야 합니다';
    }

    if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다';
    }

    const phoneRegex = /^010-\d{4}-\d{4}$/;
    if (!formData.phone) {
      newErrors.phone = '연락처를 입력해주세요';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = '올바른 전화번호 형식이 아닙니다 (010-XXXX-XXXX)';
    } else if (!phoneVerified) {
      newErrors.phone = '휴대폰 인증을 완료해주세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 사업장 정보(3단계) 유효성 검사 — 함수 이름은 옛 순서 그대로
  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.organization_name.trim()) {
      newErrors.organization_name = '독서실/학원명을 입력해주세요';
    }

    const businessNumberRegex = /^\d{3}-\d{2}-\d{5}$/;
    if (!formData.business_registration_number) {
      newErrors.business_registration_number = '사업자등록번호를 입력해주세요';
    } else if (!businessNumberRegex.test(formData.business_registration_number)) {
      newErrors.business_registration_number = '올바른 사업자등록번호 형식이 아닙니다 (XXX-XX-XXXXX)';
    }

    if (!formData.business_name.trim()) {
      newErrors.business_name = '사업자명(상호)을 입력해주세요';
    }

    if (!formData.address.trim()) {
      newErrors.address = '사업장 주소를 입력해주세요';
    }

    const phoneRegex = /^0\d{1,2}-\d{3,4}-\d{4}$/;
    if (!formData.organization_phone) {
      newErrors.organization_phone = '사업장 전화번호를 입력해주세요';
    } else if (!phoneRegex.test(formData.organization_phone)) {
      newErrors.organization_phone = '올바른 전화번호 형식이 아닙니다';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 다음 단계로
  const handleNext = () => {
    if (step === 1 && allRequiredAgreed) {
      setStep(2);
    } else if (step === 2 && validateStep1()) {
      setStep(3);
    }
  };

  // 이전 단계로
  const handleBack = () => {
    setStep(prev => (prev === 3 ? 2 : 1));  // 3→2→1(약관 동의). 동의 체크는 그대로 남는다
  };

  // 가입 신청 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1·2단계에서 엔터를 누르면 제출이 아니라 다음 단계로
    if (step !== 3) {
      handleNext();
      return;
    }

    if (!validateStep1() || !validateStep2()) {
      return;
    }

    if (!allRequiredAgreed) {
      alert('필수 항목 4가지에 모두 동의해주세요');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/owner/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone.replace(/[^0-9]/g, ''),
          organization_name: formData.organization_name,
          business_registration_number: formData.business_registration_number.replace(/[^0-9]/g, ''),
          business_name: formData.business_name,
          address: formData.address,
          organization_phone: formData.organization_phone.replace(/[^0-9]/g, ''),
          agreed_terms: agreedTerms,
          agreed_privacy: agreedPrivacy,
          agreed_entrustment: agreedEntrustment,
          agreed_pricing: agreedPricing,
          agreed_marketing: agreedMarketing,
          phone_verified_token: phoneVerifiedToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || '가입 신청에 실패했습니다');
      }

      setSubmitSuccess(true);

      // 3초 후 로그인 페이지로 이동
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error) {
      console.error('가입 신청 오류:', error);
      alert(error instanceof Error ? error.message : '가입 신청 중 오류가 발생했습니다');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 성공 화면
  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            가입 신청이 완료되었습니다!
          </h2>
          <p className="text-gray-600 mb-6">
            관리자 승인 후 로그인이 가능합니다.<br />
            승인 여부는 본부에서 담당자 연락처로 안내드리겠습니다.
          </p>
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>신청 정보</strong><br />
              이메일: {formData.email}<br />
              독서실/학원명: {formData.organization_name}
            </p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            로그인 페이지로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            뉴어텐드 구독 신청
          </h1>
          <p className="text-gray-600">
            독서실/학원 관리를 위한 원장 계정 신청 · {termsInfo.plan_name} {termsInfo.fee_label}
          </p>
        </div>

        {/* 진행 단계 표시 */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              1
            </div>
            <span className="ml-2 mr-4 text-sm font-medium text-gray-700">
              요금·약관 동의
            </span>
          </div>

          <div className="w-10 h-1 bg-gray-300 mx-2" />

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              2
            </div>
            <span className="ml-2 mr-4 text-sm font-medium text-gray-700">
              담당자 정보
            </span>
          </div>

          <div className="w-10 h-1 bg-gray-300 mx-2" />

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              3
            </div>
            <span className="ml-2 text-sm font-medium text-gray-700">
              사업장 정보
            </span>
          </div>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit}>
          {/* 1단계: 요금 안내 + 약관 동의 — 2026-10-27 대표 지시 "구독 신청을 누르면 가입 약관 동의를 통해서" (종전 3단계에서 옮김). 동의 기록은 종전처럼 가입 완료 시 서버가 저장 */}
          {step === 1 && (
            <div className="space-y-4">
              {/* 요금 안내 — 약관 제6조·제7조·제10조 요약 */}
              <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-700 font-semibold mb-1">요금 안내</p>
                <p className="text-xl font-bold text-gray-900">
                  {termsInfo.plan_name} {termsInfo.fee_label}
                </p>
                <ul className="mt-2 text-sm text-gray-700 space-y-1 list-disc pl-5">
                  <li>매월 1일 그 달 분 청구, <strong>매월 10일까지</strong> 계좌 납부</li>
                  <li>카카오 알림톡은 별도 — 지난달 발송 성공 건수 × 건당 13원(부가세 별도)</li>
                  <li><strong>약정 없음</strong> · 월 단위 자동 연장 · 언제든 해지</li>
                  <li>태블릿·출입문 장비와 설치는 별도 견적</li>
                </ul>
                <p className="mt-2 text-xs text-gray-500">
                  본부 승인 후 이용할 수 있습니다. 문의: {termsInfo.support_email}
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                {/* 전체 동의 */}
                <label className="flex items-center gap-3 p-4 cursor-pointer bg-gray-50 rounded-t-lg">
                  <input
                    type="checkbox"
                    checked={allRequiredAgreed && agreedMarketing}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setAgreedTerms(v);
                      setAgreedPrivacy(v);
                      setAgreedEntrustment(v);
                      setAgreedPricing(v);
                      setAgreedMarketing(v);
                    }}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-base font-semibold text-gray-800">
                    약관에 모두 동의합니다
                  </span>
                </label>

                {/* 이용약관 (필수) */}
                <div className="flex items-center justify-between gap-3 p-4">
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="text-blue-600 font-medium">[필수]</span> 서비스 구독 이용약관에 동의합니다
                    </span>
                  </label>
                  <a
                    href="/terms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 hover:text-blue-600 underline shrink-0"
                  >
                    전문 보기
                  </a>
                </div>

                {/* 개인정보 (필수) */}
                <div className="flex items-center justify-between gap-3 p-4">
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={agreedPrivacy}
                      onChange={(e) => setAgreedPrivacy(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="text-blue-600 font-medium">[필수]</span> 원장(담당자) 개인정보 수집·이용에 동의합니다
                    </span>
                  </label>
                  <a
                    href="/privacy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 hover:text-blue-600 underline shrink-0"
                  >
                    전문 보기
                  </a>
                </div>

                {/* 학생·학부모 정보 처리위탁 (필수) — 약관 제13조·제14조 */}
                <div className="flex items-center justify-between gap-3 p-4">
                  <label className="flex items-start gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={agreedEntrustment}
                      onChange={(e) => setAgreedEntrustment(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="text-blue-600 font-medium">[필수]</span> 학생·학부모 개인정보 처리를 회사에 맡기며(처리위탁),
                      학생·학부모 동의는 매장이 직접 받는 것에 동의합니다
                    </span>
                  </label>
                  <a
                    href="/terms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 hover:text-blue-600 underline shrink-0"
                  >
                    제13·14조
                  </a>
                </div>

                {/* 요금 및 결제 조건 (필수) — 약관 제6조·제7조 */}
                <div className="flex items-center justify-between gap-3 p-4">
                  <label className="flex items-start gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={agreedPricing}
                      onChange={(e) => setAgreedPricing(e.target.checked)}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      <span className="text-blue-600 font-medium">[필수]</span> 요금 및 결제 조건({termsInfo.plan_name} {termsInfo.fee_label},
                      매월 10일 납부, 알림톡 건당 13원 별도)에 동의합니다
                    </span>
                  </label>
                  <a
                    href="/terms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 hover:text-blue-600 underline shrink-0"
                  >
                    제6·7조
                  </a>
                </div>

                {/* 마케팅 (선택) */}
                <label className="flex items-center gap-3 p-4 cursor-pointer rounded-b-lg">
                  <input
                    type="checkbox"
                    checked={agreedMarketing}
                    onChange={(e) => setAgreedMarketing(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="text-gray-500 font-medium">[선택]</span> 서비스 소식 및 혜택 정보 수신에 동의합니다
                  </span>
                </label>
              </div>

              {/* 학생·학부모 동의 안내 — 매장이 직접 받아야 하는 부분 */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-900">
                  <strong>학생·학부모 동의는 원에서 직접 받으셔야 합니다</strong><br />
                  학생 정보를 등록하기 전에 개인정보 수집·이용 동의를 받아주세요.
                  특히 <strong>얼굴 인식 정보</strong>는 별도 동의가 필요하며,
                  만 14세 미만 학생은 학부모 동의가 필요합니다.
                </p>
              </div>

              {!allRequiredAgreed && (
                <p className="text-sm text-gray-500 text-center">필수 항목 4가지에 동의하시면 다음 단계(담당자·사업장 정보)로 넘어갑니다.</p>
              )}

              {/* 버튼 */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!allRequiredAgreed}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  동의하고 다음
                </button>
              </div>
            </div>
          )}

          {/* 2단계: 담당자 정보 */}
          {step === 2 && (
            <div className="space-y-4">
              {/* 이름 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  담당자 이름 *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="홍길동"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* 이메일 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <EnvelopeIcon className="w-4 h-4 inline mr-1" />
                  이메일 (로그인 ID) *
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.email
                        ? 'border-red-500'
                        : emailCheckStatus === 'available'
                        ? 'border-green-500'
                        : emailCheckStatus === 'taken'
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                    placeholder="example@email.com"
                  />
                  {/* 이메일 확인 상태 아이콘 */}
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {emailCheckStatus === 'checking' && (
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    )}
                    {emailCheckStatus === 'available' && (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    )}
                    {emailCheckStatus === 'taken' && (
                      <XCircleIcon className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
                {!errors.email && emailCheckStatus === 'available' && (
                  <p className="mt-1 text-sm text-green-600">✓ 사용 가능한 이메일입니다</p>
                )}
                {!errors.email && emailCheckStatus === 'taken' && (
                  <p className="mt-1 text-sm text-red-600">✗ 이미 사용 중인 이메일입니다</p>
                )}
              </div>

              {/* 비밀번호 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비밀번호 *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="8자 이상"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              {/* 비밀번호 확인 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  비밀번호 확인 *
                </label>
                <input
                  type="password"
                  name="passwordConfirm"
                  value={formData.passwordConfirm}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.passwordConfirm ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="비밀번호 재입력"
                />
                {errors.passwordConfirm && (
                  <p className="mt-1 text-sm text-red-600">{errors.passwordConfirm}</p>
                )}
              </div>

              {/* 연락처 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <PhoneIcon className="w-4 h-4 inline mr-1" />
                  연락처 *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange('phone')}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="010-0000-0000"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>

              {/* 휴대폰 카톡 인증 (2026-10-27 밤 — 이메일 인증 대신) */}
              {!phoneVerified ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 mb-1">
                    📱 휴대폰 인증이 필요합니다
                  </p>
                  <p className="text-xs text-blue-800 mb-3">
                    위 연락처로 카카오톡 인증번호를 보내 드립니다.
                  </p>
                  {!codeSent ? (
                    <button
                      type="button"
                      onClick={handleSendVerificationCode}
                      disabled={isCodeSending}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {isCodeSending ? '보내는 중...' : '카카오톡으로 인증번호 받기'}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest"
                        placeholder="인증번호 6자리"
                        maxLength={6}
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleVerifyCode}
                          disabled={isCodeVerifying || verificationCode.length !== 6}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          {isCodeVerifying ? '확인 중...' : '인증 확인'}
                        </button>
                        <button
                          type="button"
                          onClick={handleSendVerificationCode}
                          disabled={isCodeSending}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          다시 받기
                        </button>
                      </div>
                    </div>
                  )}
                  {phoneMsg && (
                    <p className={`mt-2 text-sm ${phoneMsg.type === 'ok' ? 'text-blue-800' : 'text-red-600'}`}>
                      {phoneMsg.text}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-900 flex items-center">
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    휴대폰 인증이 완료되었습니다
                  </p>
                </div>
              )}

              {/* 다음 버튼 */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  이전
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  다음
                </button>
              </div>
            </div>
          )}

          {/* 3단계: 사업장 정보 → 가입 신청 */}
          {step === 3 && (
            <div className="space-y-4">
              {/* 독서실/학원명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <BuildingOffice2Icon className="w-4 h-4 inline mr-1" />
                  독서실/학원명 *
                </label>
                <input
                  type="text"
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.organization_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="예: 강남스터디룸"
                />
                {errors.organization_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.organization_name}</p>
                )}
              </div>

              {/* 사업자등록번호 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <DocumentTextIcon className="w-4 h-4 inline mr-1" />
                  사업자등록번호 *
                </label>
                <input
                  type="text"
                  name="business_registration_number"
                  value={formData.business_registration_number}
                  onChange={handleBusinessNumberChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.business_registration_number ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="000-00-00000"
                />
                {errors.business_registration_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.business_registration_number}</p>
                )}
              </div>

              {/* 사업자명 (상호) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  사업자명 (상호) *
                </label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.business_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="예: (주)강남에듀"
                />
                {errors.business_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.business_name}</p>
                )}
              </div>

              {/* 사업장 주소 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPinIcon className="w-4 h-4 inline mr-1" />
                  사업장 주소 *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.address ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="예: 서울시 강남구 테헤란로 123"
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address}</p>
                )}
              </div>

              {/* 사업장 전화번호 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <PhoneIcon className="w-4 h-4 inline mr-1" />
                  사업장 전화번호 *
                </label>
                <input
                  type="tel"
                  name="organization_phone"
                  value={formData.organization_phone}
                  onChange={handlePhoneChange('organization_phone')}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.organization_phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="02-0000-0000"
                />
                {errors.organization_phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.organization_phone}</p>
                )}
              </div>

              {/* 안내 메시지 */}
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>안내</strong><br />
                  신청하신 정보는 관리자 검토 후 승인됩니다.<br />
                  승인 여부는 본부에서 담당자 연락처로 안내드립니다.
                </p>
              </div>

              {/* 버튼 */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  이전
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !allRequiredAgreed}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? '신청 중...' : '가입 신청'}
                </button>
              </div>
            </div>
          )}

        </form>

        {/* 하단 링크 */}
        <div className="mt-6 text-center text-sm text-gray-600">
          이미 계정이 있으신가요?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:underline"
          >
            로그인하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnerSignupPage;
